import React, { useCallback, useEffect, useState } from "react";
import api, { formatTanggal, uploadFile } from "@/lib/api";
import Layout, { PageTitle } from "@/components/Layout";
import { NAV_KEPSEK } from "@/lib/nav";
import { FilterBar } from "@/components/FilterBar";
import { Eye, Trash2, Pencil, Upload, X } from "lucide-react";
import { toast } from "sonner";
import PdfViewerModal from "@/components/PdfViewerModal";

const PROGRAM_LABEL = { ko_kurikuler: "Ko-Kurikuler", intra_kurikuler: "Intra-Kurikuler", ekstra_kurikuler: "Ekstra-Kurikuler" };
const PROGRAM_OPTS = [
  { v: "ko_kurikuler", label: "Ko-Kurikuler" },
  { v: "intra_kurikuler", label: "Intra-Kurikuler" },
  { v: "ekstra_kurikuler", label: "Ekstra-Kurikuler" },
];
const TAHUN_OPTS = ["2023/2024", "2024/2025", "2025/2026", "2026/2027"];
const F_EMPTY = { guru: "", program: "", tahun_ajaran: "", semester: "", mapel: "" };

export default function ModulAjarKepsek() {
  const [all, setAll] = useState([]);
  const [filters, setFilters] = useState(F_EMPTY);
  const [applied, setApplied] = useState(F_EMPTY);
  const [viewer, setViewer] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/modul-ajar"); setAll(data); }
    catch (e) { console.error(e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const items = all.filter((m) => {
    if (applied.guru && !(m.guru_nama || "").toLowerCase().includes(applied.guru.toLowerCase())) return false;
    if (applied.program && m.program !== applied.program) return false;
    if (applied.tahun_ajaran && m.tahun_ajaran !== applied.tahun_ajaran) return false;
    if (applied.semester && m.semester !== applied.semester) return false;
    if (applied.mapel && !m.mata_pelajaran.toLowerCase().includes(applied.mapel.toLowerCase())) return false;
    return true;
  });

  const del = async (id) => {
    if (!window.confirm("Hapus modul ajar ini?")) return;
    try { await api.delete(`/modul-ajar/${id}`); toast.success("Dihapus"); load(); }
    catch (e) { toast.error("Gagal hapus"); }
  };

  const uploadNewFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try { const r = await uploadFile(f); setEditItem({ ...editItem, file_id: r.id }); toast.success("File baru diunggah"); }
    catch (err) { toast.error(err.response?.data?.detail || "Gagal upload"); }
    finally { setUploading(false); }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/modul-ajar/${editItem.id}`, {
        tahun_ajaran: editItem.tahun_ajaran,
        semester: editItem.semester,
        program: editItem.program,
        mata_pelajaran: editItem.mata_pelajaran,
        file_id: editItem.file_id,
      });
      toast.success("Modul ajar diperbarui");
      setEditItem(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal menyimpan"); }
    finally { setSaving(false); }
  };

  return (
    <Layout nav={NAV_KEPSEK}>
      <PageTitle title="Modul Ajar (Semua Guru)" />
      <FilterBar
        filters={filters} setFilters={setFilters}
        onApply={() => setApplied({ ...filters })}
        onReset={() => { setFilters(F_EMPTY); setApplied(F_EMPTY); }}
        dataTestidPrefix="modul-filter"
        fields={[
          { key: "guru", type: "text", label: "Guru", placeholder: "Cari guru" },
          { key: "program", type: "select", label: "Program", options: PROGRAM_OPTS },
          { key: "tahun_ajaran", type: "select", label: "Tahun Ajaran", options: TAHUN_OPTS },
          { key: "semester", type: "select", label: "Semester", options: [{ v: "I", label: "Semester I" }, { v: "II", label: "Semester II" }] },
          { key: "mapel", type: "text", label: "Mata Pelajaran", placeholder: "Cari mapel" },
        ]}
      />
      <section className="pp-card pp-freeze overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="pp-table-header">
            <th className="text-left px-5 py-3">Tanggal Upload</th>
            <th className="text-left px-5 py-3">Guru</th>
            <th className="text-left px-5 py-3">Periode</th>
            <th className="text-left px-5 py-3">Program</th>
            <th className="text-left px-5 py-3">Mata Pelajaran</th>
            <th className="text-left px-5 py-3">File</th>
            <th className="text-right px-5 py-3">Aksi</th>
          </tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-slate-400 italic">Belum ada modul ajar.</td></tr>}
            {items.map((m) => (
              <tr key={m.id} className="pp-row">
                <td className="px-5 py-3 text-xs">{formatTanggal(m.created_at)}</td>
                <td className="px-5 py-3 font-medium">{m.guru_nama || "—"}</td>
                <td className="px-5 py-3">{m.tahun_ajaran} · Sem {m.semester}</td>
                <td className="px-5 py-3"><span className="pp-badge-info">{PROGRAM_LABEL[m.program]}</span></td>
                <td className="px-5 py-3">{m.mata_pelajaran}</td>
                <td className="px-5 py-3">
                  <button onClick={() => setViewer({ id: m.file_id, title: `${m.mata_pelajaran} · ${m.guru_nama || "—"}` })} className="pp-btn-ghost text-xs" data-testid={`view-modul-kepsek-${m.id}`}>
                    <Eye className="w-3.5 h-3.5" /> Lihat
                  </button>
                </td>
                <td className="px-5 py-3 text-right whitespace-nowrap">
                  <button onClick={() => setEditItem({ ...m })} className="pp-btn-ghost text-xs mr-1" data-testid={`modul-edit-${m.id}`}><Pencil className="w-3.5 h-3.5" />Edit</button>
                  <button onClick={() => del(m.id)} className="pp-btn-danger" data-testid={`modul-del-${m.id}`}><Trash2 className="w-3.5 h-3.5" />Hapus</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {editItem && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="modul-edit-modal">
          <div className="pp-card p-6 w-full max-w-lg animate-fade-up">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg">Edit Modul Ajar — {editItem.guru_nama || ""}</h3>
              <button onClick={() => setEditItem(null)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <form onSubmit={saveEdit} className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="pp-label">Tahun Ajaran</label>
                <select className="pp-input" value={editItem.tahun_ajaran} onChange={(e) => setEditItem({ ...editItem, tahun_ajaran: e.target.value })} data-testid="modul-edit-ta">
                  {TAHUN_OPTS.map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="pp-label">Semester</label>
                <select className="pp-input" value={editItem.semester} onChange={(e) => setEditItem({ ...editItem, semester: e.target.value })} data-testid="modul-edit-sem">
                  <option value="I">Semester I</option>
                  <option value="II">Semester II</option>
                </select>
              </div>
              <div>
                <label className="pp-label">Program</label>
                <select className="pp-input" value={editItem.program} onChange={(e) => setEditItem({ ...editItem, program: e.target.value })} data-testid="modul-edit-program">
                  {PROGRAM_OPTS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label className="pp-label">Mata Pelajaran</label>
                <input className="pp-input" value={editItem.mata_pelajaran} onChange={(e) => setEditItem({ ...editItem, mata_pelajaran: e.target.value })} data-testid="modul-edit-mapel" />
              </div>
              <div className="sm:col-span-2">
                <label className="pp-label">Ganti File PDF (opsional)</label>
                <label className="pp-btn-secondary cursor-pointer">
                  <Upload className="w-4 h-4" />{uploading ? "Mengunggah…" : "Pilih PDF baru"}
                  <input type="file" accept=".pdf" onChange={uploadNewFile} className="hidden" data-testid="modul-edit-file" />
                </label>
                <span className="ml-3 text-xs text-slate-500">File saat ini tetap dipakai jika tidak diganti</span>
              </div>
              <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setEditItem(null)} className="pp-btn-secondary">Batal</button>
                <button type="submit" disabled={saving} className="pp-btn-primary" data-testid="modul-edit-save">{saving ? "Menyimpan…" : "Simpan"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {viewer && <PdfViewerModal fileId={viewer.id} title={viewer.title} onClose={() => setViewer(null)} />}
    </Layout>
  );
}
